/**
 * Testi italiani del dominio agenti: stati, profili, transizioni di un set,
 * agenti di un set, catalogo dei ruoli.
 *
 * Dal 2026-09-26 (ADR 0008) i testi della pagina di un set stanno in
 * `sets.ts`, `setProjects.ts`, `conversation.ts` e `setup.ts`; qui restano i
 * valori enumerati e i testi degli agenti, condivisi da più pagine.
 *
 * Le transizioni di stato sono frasi intere e non un verbo da comporre con il
 * nome del set: «Archiviare il set Squadra?» in italiano e «Archive the
 * Squadra set?» in inglese non hanno lo stesso ordine delle parole, e una
 * concatenazione lo imporrebbe a entrambe.
 */

/** Testi del dominio agenti. */
export const agents = {
  /** Stato di un set di agenti. */
  setStatus: {
    /** Set creato e non ancora attivato. */
    draft: 'Bozza',
    /** Set attivo: gli agenti si possono avviare. */
    active: 'Attivo',
    /** Set sospeso: gli agenti sono fermi. */
    suspended: 'Sospeso',
    /** Set uscito dall'uso. */
    archived: 'Archiviato',
  },

  /** Stato richiesto per un agente. */
  desiredState: {
    /** Agente fermo. */
    stopped: 'Fermo',
    /** Agente in esecuzione. */
    running: 'In esecuzione',
  },

  /** Stato effettivo di un agente, riferito dal runtime. */
  observedState: {
    /** Nessuna osservazione disponibile. */
    unknown: 'Non rilevato',
    /** Il runtime non ha risorse per questo agente. */
    absent: 'Non creato',
    /** Sta partendo. */
    starting: 'In avvio',
    /** Gira ed è pronto. */
    running: 'In esecuzione',
    /** Si sta fermando. */
    stopping: 'In arresto',
    /** Risorse presenti, nessun processo in esecuzione. */
    stopped: 'Fermo',
    /** Doveva girare e non ci riesce. */
    failed: 'In errore',
  },

  /** Profilo di modello di un agente. */
  profile: {
    /** Profilo normale. */
    default: 'Normale',
    /** Profilo economico. */
    cheap: 'Economico',
  },

  /** Modalità di accesso di un set a un progetto collegato. */
  mode: {
    /** Sola lettura. */
    read: 'Lettura',
    /** Lettura e scrittura. */
    write: 'Scrittura',
  },

  /** Transizioni di stato proponibili su un set. */
  transitions: {
    /** Da bozza ad attivo. */
    activate: {
      /** Etichetta del pulsante. */
      label: 'Attiva',
      /** Domanda della conferma, con il nome del set. */
      question: 'Attivare il set {{name}}?',
      /** Che cosa succede davvero. */
      consequence: 'Gli agenti del set possono essere avviati.',
    },
    /** Da attivo a sospeso. */
    suspend: {
      /** Etichetta del pulsante. */
      label: 'Sospendi',
      /** Domanda della conferma. */
      question: 'Sospendere il set {{name}}?',
      /** Che cosa succede davvero. */
      consequence: 'Gli agenti in esecuzione si fermano.',
    },
    /** Da sospeso ad attivo. */
    reactivate: {
      /** Etichetta del pulsante. */
      label: 'Riattiva',
      /** Domanda della conferma. */
      question: 'Riattivare il set {{name}}?',
      /** Che cosa succede davvero. */
      consequence: 'Gli agenti del set possono essere avviati di nuovo.',
    },
    /** Verso l'archiviazione: è terminale. */
    archive: {
      /** Etichetta del pulsante. */
      label: 'Archivia',
      /** Domanda della conferma. */
      question: 'Archiviare il set {{name}}?',
      /** Che cosa succede davvero. */
      consequence: "Gli agenti si fermano e il set esce dall'uso. Non si riattiva da qui.",
    },
  },

  /** Cambi di stato di un set. */
  set: {
    /** Cambio di stato riuscito. */
    statusUpdated: 'Stato aggiornato.',
    /** Cambio di stato fallito. */
    statusFailed: 'Cambio di stato non riuscito. Riprova.',
  },

  /** Pannello degli agenti di un set. */
  members: {
    /** Titolo del pannello. */
    title: 'Agenti del set',
    /** Pulsante che apre il cassetto di aggiunta. */
    create: 'Nuovo agente',
    /** Avvertenza quando il set non è attivo. */
    notRunnable: 'Gli agenti si avviano solo quando il set è attivo.',
    /** Nessun agente nel set. */
    empty: 'Nessun agente nel set.',
    /** Colonne della tabella. */
    columns: {
      /** Ruolo dell'agente, dal catalogo. */
      role: 'Ruolo',
      /** Profilo di modello. */
      profile: 'Profilo del modello',
      /** Stato richiesto. */
      desiredState: 'Stato richiesto',
      /** Stato riferito dal runtime. */
      observedState: 'Stato effettivo',
    },
    /** Avvio di un agente. */
    start: {
      /** Etichetta del pulsante. */
      label: 'Avvia',
      /** Nome accessibile della riga. */
      accessibleName: 'Avvia {{name}}',
      /** Esito riuscito. */
      done: '{{name}} avviato.',
    },
    /** Arresto di un agente. */
    stop: {
      /** Etichetta del pulsante. */
      label: 'Ferma',
      /** Nome accessibile della riga. */
      accessibleName: 'Ferma {{name}}',
      /** Domanda della conferma. */
      question: 'Fermare {{name}}?',
      /** Che cosa succede davvero. */
      consequence: "Il lavoro in corso si interrompe. L'agente resta nel set e si può riavviare.",
      /** Esito riuscito. */
      done: '{{name}} fermato.',
    },
    /** Cassetto di aggiunta di un agente. */
    form: {
      /** Titolo del cassetto e nome accessibile del form. */
      title: 'Nuovo agente',
      /** Pulsante di invio. */
      submit: 'Aggiungi agente',
      /** Etichetta del selettore del ruolo. */
      role: 'Ruolo',
      /** Testo di riempimento del selettore. */
      rolePlaceholder: 'Scegli un ruolo',
      /** Etichetta del campo del nome. */
      name: "Nome dell'agente",
      /** Vincoli del nome. */
      nameHelp: 'Minuscole, cifre e trattini. Deve essere diverso dagli altri agenti del set.',
      /** Etichetta del selettore del harness. */
      harness: 'Harness',
      /** Esito riuscito. */
      done: 'Agente aggiunto.',
    },
  },

  /** Catalogo dei ruoli degli agenti. */
  roles: {
    /** Titolo della pagina. */
    title: 'Ruoli degli agenti',
    /** Frase che dice a che cosa serve la pagina. */
    description:
      "I ruoli che un agente può assumere. L'elenco arriva dal catalogo della piattaforma e non si modifica da qui.",
    /** Catalogo vuoto. */
    empty: 'Nessun ruolo nel catalogo.',
    /** Lettura non riuscita. */
    loadFailed: 'Caricamento dei ruoli non riuscito. Riprova.',
    /** Colonne della tabella. */
    columns: {
      /** Nome leggibile del ruolo. */
      role: 'Ruolo',
      /** Chiave tecnica del ruolo. */
      key: 'Chiave',
      /** Che cosa fa l'agente con questo ruolo. */
      description: 'Che cosa fa',
      /** Profilo di modello proposto dal catalogo. */
      profile: 'Profilo proposto',
    },
  },
};

/** Forma dei testi degli agenti, usata per tipare le altre lingue. */
export type Agents = typeof agents;
